<template>
  <div>
    <div v-if="selectedInvoice">
      <!-- Outer Section Wrapper -->
      <v-card flat class="my-2 my-sm-4 bg-transparent">
        
        <!-- Header Banner Section -->
        <v-card outlined class="mb-4 pa-3 pa-sm-4 rounded-lg elevation-1 elevation-sm-0">
          <v-row align="center" no-gutters class="gy-2">

            <!-- Left Action Button -->
            <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
              <v-btn 
                block 
                sm-only="false" 
                elevation="11" 
                rounded 
                color="success" 
                depressed 
                @click="addBillingEntry" 
                class="px-4 text-capitalize font-weight-bold"
              >
                <v-icon left>mdi-plus</v-icon>
                Add {{ itemLabel }} Item
              </v-btn>
            </v-col>

            <v-spacer class="d-none d-sm-block"></v-spacer>
            <h2 class="text-h6 text-sm-h5 font-weight-bold text-center text-sm-left my-1 my-sm-0" :style="{ color: getThemeColor }">
              {{ itemLabel }} Items
            </h2>
            <v-spacer class="d-none d-sm-block"></v-spacer>

            <!-- Total Callout Badge -->
            <v-col cols="12" sm="auto">
              <v-sheet 
                outlined 
                rounded="lg" 
                color="green lighten-5" 
                class="d-flex align-center justify-space-between justify-sm-start px-4 py-2 border-green"
              >
                <div class="d-flex align-center">
                  <v-icon color="green darken-2" class="mr-3">mdi-cash-multiple</v-icon>
                  <span class="text-caption grey--text text--darken-2 font-weight-bold text-uppercase tracking-wide">
                    Total {{ itemLabel }}
                  </span>
                </div>
                <span class="text-h6 font-weight-black green--text text--darken-3 ml-2">
                  {{ formatCurrency(totalBillable) }}
                </span>
              </v-sheet>
            </v-col>

          </v-row>
        </v-card>

        <!-- Filters Section -->
        <div class="px-1 mb-3">
          <v-row dense align="center">
            <v-col cols="6" sm="4" md="3">
              <v-select 
                v-model="ledgerFilter" 
                :items="['All', 'Fee', 'Disbursement']" 
                label="Filter by Type"
                hide-details 
                outlined 
                dense 
                background-color="white"
                class="rounded-lg"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="8" md="9">
              <v-text-field 
                v-model="billingSearch" 
                append-icon="mdi-magnify" 
                label="Search items..." 
                single-line
                hide-details 
                outlined 
                dense 
                background-color="white"
                class="rounded-lg"
              ></v-text-field>
            </v-col>
          </v-row>
        </div>

        <!-- ============================================================ -->
        <!-- 1. DESKTOP / TABLET VIEW: DATA TABLE (Visible on smAndUp)    -->
        <!-- ============================================================ -->
        <v-card-text class="d-none d-sm-block pa-0">
          <v-data-table 
            :headers="billingHeaders" 
            :items="filteredBillingEntries" 
            dense 
            outlined
            class="elevation-1 rounded-lg"
          >
            <template v-slot:item.id="{ item }">
              <v-btn icon small color="error" @click="openDeleteBillingEntryDialog(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:item.price="{ item }">
              {{ formatCurrency(item.price) }}
            </template>
            
            <template v-slot:item.tax_amount="{ item }">
              {{ formatCurrency(calculateVAT(item)) }}
            </template>
            
            <template v-slot:item.lineTotal="{ item }">
              <span class="font-weight-bold">
                {{ formatCurrency(getItemTotal(item)) }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>

        <!-- ============================================================ -->
        <!-- 2. NATIVE MOBILE VIEW: CARDS (Visible on xsOnly)              -->
        <!-- ============================================================ -->
        <div class="d-sm-none">
          <div v-if="filteredBillingEntries.length === 0" class="text-center py-8 grey--text">
            <v-icon size="48" color="grey lighten-1">mdi-file-remove-outline</v-icon>
            <p class="mt-2 text-body-2">No billing items found.</p>
          </div>

          <v-card
            v-for="item in filteredBillingEntries"
            :key="item.id"
            elevation="10"
            class="mb-4 mx-2 rounded-xl overflow-hidden"
            outlined
          >
            <v-card-text class="pa-4">
              <!-- Item Header Row -->
              <div class="d-flex justify-space-between align-start mb-2">
                <div>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-4 d-block line-height-tight">
                    {{ item.name }}
                  </span>
                  <v-chip
                    v-if="item.type"
                    x-small
                    label
                    color="primary lighten-5"
                    text-color="primary"
                    class="font-weight-bold mt-1"
                  >
                    {{ item.type }}
                  </v-chip>
                </div>

                <!-- Action Button -->
                <v-btn
                  icon
                  small
                  color="red lighten-1"
                  class="ml-2 grey lighten-4"
                  @click="openDeleteBillingEntryDialog(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>

              <v-divider class="my-2"></v-divider>

              <!-- Line Details Breakdown -->
              <div class="d-flex justify-space-between text-body-2 grey--text text--darken-2 mb-1">
                <span>Unit Price & Qty:</span>
                <span class="font-weight-medium">
                  {{ formatCurrency(item.price) }} × {{ item.quantity || 1 }}
                </span>
              </div>

              <div class="d-flex justify-space-between text-body-2 grey--text text--darken-2 mb-2">
                <span>VAT ({{ getVat.label }}):</span>
                <span class="font-weight-medium">
                  {{ formatCurrency(calculateVAT(item)) }}
                </span>
              </div>

              <!-- Card Bottom Total Bar -->
              <div class="d-flex justify-space-between align-center pt-2 border-top-dashed">
                <span class="text-caption font-weight-bold text-uppercase grey--text">Total</span>
                <span class="text-subtitle-1 font-weight-black green--text text--darken-2">
                  {{ formatCurrency(getItemTotal(item)) }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </div>

      </v-card>
    </div>

    <!-- Modals -->
    <billing-entry-dialog v-model="showBillingDialog" @save="handleNewEntry" />

    <v-dialog persistent max-width="480px" v-model="deleteBillingEntryDialog">
      <v-card class="rounded-lg overflow-hidden">
        <v-card-title class="red darken-1 white--text text-h6">
          <v-icon color="white" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4 text-body-1">
          Are you sure you want to delete <strong>{{ deleteBillingEntryItem ? deleteBillingEntryItem.name : '' }}</strong>?
          <div class="mt-2 grey--text text--darken-1 text-caption">
            This action cannot be undone.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 bg-grey-lighten">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-2" @click="closeDeleteBillingDialog">Cancel</v-btn>
          <v-btn color="red" dark elevation="2" @click="deleteBillingNow(deleteBillingEntryItem.id)">
            Delete Entry
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from '@/plugins/helpers';
import BillingEntryDialog from '@/modules/CaseManager/BillingEntryDialog.vue';

export default {
  name: "BillingItemTable",
  components: { BillingEntryDialog },
  props: {
    selectedInvoice: {
      type: Object,
      default: null
    },
    itemLabel: {
      type: String,
      default: "Invoice"
    },
    referenceNumber: {
      type: String,
      default: "invoice_number"
    }
  },
  data: () => ({
    billingSearch: '',
    ledgerFilter: "All",
    showBillingDialog: false,
    deleteBillingEntryItem: null,
    deleteBillingEntryDialog: false
  }),
  computed: {
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getVat() {
      return this.$store.getters.getVat || { label: 'VAT', value: 0 };
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    billingHeaders() {
      return [
        { text: "Action", value: "id", sortable: false, width: "60px" },
        { text: 'Description', value: 'name' },
        { text: 'Amount (Excl)', value: 'price' },
        { text: 'Qty', value: 'quantity' },
        { text: `VAT (${this.getVat.label})`, value: 'tax_amount', sortable: false },
        { text: 'Total', value: 'lineTotal', sortable: false }
      ];
    },
    billingEntries() {
      return (this.selectedInvoice && this.selectedInvoice.billingEntries) ? this.selectedInvoice.billingEntries : [];
    },
    filteredBillingEntries() {
      return this.billingEntries.filter(item => {
        // 1. Type filter
        if (this.ledgerFilter !== 'All' && item.type !== this.ledgerFilter) {
          return false;
        }

        // 2. Search filter
        if (this.billingSearch) {
          const searchLower = this.billingSearch.toLowerCase();
          const nameMatch = item.name ? item.name.toLowerCase().includes(searchLower) : false;
          const priceMatch = item.price ? item.price.toString().includes(searchLower) : false;
          return nameMatch || priceMatch;
        }

        return true;
      });
    },
    totalBillable() {
      return this.billingEntries.reduce((sum, entry) => {
        return sum + this.getItemTotal(entry);
      }, 0);
    }
  },
  methods: {
    addBillingEntry() {
      this.showBillingDialog = true;
    },
    formatCurrency(value) {
      if (!value || isNaN(value)) {
        return this.getCurrency + '0.00';
      }
      return this.getCurrency + helpersJs.formatMoney(value);
    },
    calculateVAT(item) {
      if (item && item.isVatable) {
        const price = Number(item.price) || 0;
        const qty = Number(item.quantity) || 1;
        const vatRate = Number(this.getVat.value) || 0;
        return price * qty * vatRate;
      }
      return 0;
    },
    getItemTotal(item) {
      if (!item) return 0;
      const price = Number(item.price) || 0;
      const qty = Number(item.quantity) || 1;
      return (price * qty) + this.calculateVAT(item);
    },
    openDeleteBillingEntryDialog(item) {
      this.deleteBillingEntryItem = item;
      this.deleteBillingEntryDialog = true;
    },
    async handleNewEntry(newEntry) {
      const updatedEntries = [...this.billingEntries, newEntry];

      this.$store.commit('setLoader', true);

      try {
        const res = await axios.postDataWithToken(
          "/auth/save-or-update-case-billings",
          {
            internalRef: this.selectedInvoice[this.referenceNumber],
            invoiceNo: this.selectedInvoice[this.referenceNumber],
            billingEntries: updatedEntries,
            mode: this.itemLabel
          },
          this.getAccessToken
        );

        if (res && res.status === "success") {
          this.$toast.success("Billing entry added successfully!", { duration: 5000 });
          this.$emit("billing-added", res.billingEntries || updatedEntries);
        } else {
          this.$toast.error('Oops, something went wrong!', { duration: 5000 });
        }
      } catch (error) {
        this.$toast.error('Failed to submit entry.', { duration: 5000 });
      } finally {
        this.$store.commit('setLoader', false);
      }
    },
    async deleteBillingNow(billingId) {
      this.$store.commit('setLoader', true);
      const table = this.itemLabel.toLowerCase() === 'invoice' ? 'invoice_items' : 'quotation_items';
      
      try {
        await helpersJs.deleteSingleRow(table, billingId);
        const updatedList = this.billingEntries.filter(entry => entry.id !== billingId);
        this.$emit("billing-added", updatedList);
      } catch (e) {
        this.$toast.error('Failed to delete item.', { duration: 5000 });
      } finally {
        this.$store.commit('setLoader', false);
        this.closeDeleteBillingDialog();
      }
    },
    closeDeleteBillingDialog() {
      this.deleteBillingEntryDialog = false;
      this.deleteBillingEntryItem = null;
    }
  }
};
</script>

<style scoped>
.border-green {
  border: 1px solid #c8e6c9 !important;
}

.border-left-accent {
  border-left: 4px solid #1976d2 !important;
}

.border-top-dashed {
  border-top: 1px dashed #e0e0e0;
}

.line-height-tight {
  line-height: 1.25;
}

.tracking-wide {
  letter-spacing: 0.5px;
}
</style>